import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, X, Trash2, Pencil, ShieldOff } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

const FLOORS = [5, 4, 3, 2, 1, -1];
const UNITS_PER_FLOOR = 10;
const DEFAULT_ADDRESS_PREFIX = '경기도 성남시 분당구 ';

function floorLabel(floor: number): string {
  return floor === -1 ? 'B1' : `${floor}F`;
}

function unitDisplayCode(floor: number, unitNumber: string): string {
  const num = Number(unitNumber);
  if (floor === -1) {
    if (!isNaN(num)) return `B${100 + num}`;
    return `B${unitNumber}`;
  }
  if (!isNaN(num) && num >= 1 && num <= 10) {
    return String(floor * 100 + num);
  }
  return `${floor}F-${unitNumber}`;
}

function parseUnitKey(key: string): { floor: number; unitNumber: string } {
  const idx = key.indexOf('_');
  return { floor: Number(key.slice(0, idx)), unitNumber: key.slice(idx + 1) };
}

function isStandardUnit(unitNumber: string): boolean {
  const num = Number(unitNumber);
  return !isNaN(num) && num >= 1 && num <= UNITS_PER_FLOOR;
}

type FieldSurvey = {
  id: number;
  address: string;
  dongName: string | null;
  memo: string | null;
  isAccessRestricted: boolean;
  createdAt: string;
  _count: { units: number };
};

type FieldSurveyDetail = FieldSurvey & {
  groupId: number | null;
  units: { floor: number; unitNumber: string }[];
};

type Step = 'address' | 'select' | 'confirm';

const FieldSurveyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editId = id ? Number(id) : null;
  const isEditMode = editId !== null;

  const [step, setStep] = useState<Step>(isEditMode ? 'select' : 'address');
  const [groupId, setGroupId] = useState<number | null>(null);
  const [address, setAddress] = useState(DEFAULT_ADDRESS_PREFIX);
  const [dongName, setDongName] = useState('');
  const [memo, setMemo] = useState('');
  const [isAccessRestricted, setIsAccessRestricted] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [customUnits, setCustomUnits] = useState<Map<number, string[]>>(new Map());
  const [loaded, setLoaded] = useState(false);

  // Fetch groups
  const { data: groupsData } = useQuery({
    queryKey: ['field-survey-groups'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });
  const groups: { id: number; name: string }[] = groupsData?.data || [];

  const { data: editData } = useQuery({
    queryKey: ['field-survey-detail', editId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${editId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
    enabled: isEditMode,
  });

  useEffect(() => {
    if (!isEditMode || loaded || !editData?.data) return;
    const survey: FieldSurveyDetail = editData.data;
    setGroupId(survey.groupId ?? null);
    setAddress(survey.address);
    setDongName(survey.dongName || '');
    setMemo(survey.memo || '');
    setIsAccessRestricted(survey.isAccessRestricted);
    const units = new Set<string>();
    const customs = new Map<number, string[]>();
    for (const u of survey.units) {
      units.add(`${u.floor}_${u.unitNumber}`);
      if (!isStandardUnit(u.unitNumber)) {
        const list = customs.get(u.floor) || [];
        list.push(u.unitNumber);
        customs.set(u.floor, list);
      }
    }
    setSelectedUnits(units);
    setCustomUnits(customs);
    setLoaded(true);
  }, [isEditMode, loaded, editData]);

  const { data: surveysData } = useQuery({
    queryKey: ['field-surveys'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys`);
      if (!res.ok) throw new Error('Failed to fetch surveys');
      return res.json();
    },
    enabled: !isEditMode,
    refetchInterval: 15000,
  });

  const surveys: FieldSurvey[] = surveysData?.data || [];

  const createMutation = useMutation({
    mutationFn: async (data: { address: string; dongName?: string; memo?: string; isAccessRestricted?: boolean; units: { floor: number; unitNumber: string }[] }) => {
      const res = await fetch(`${API_BASE}/api/field-surveys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || '저장에 실패했습니다');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      toast.success('현장 조사가 저장되었습니다');
      resetForm();
    },
    onError: (error: Error) => { toast.error(error.message); },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { address: string; dongName?: string; memo?: string; isAccessRestricted?: boolean; units: { floor: number; unitNumber: string }[] }) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${editId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || '수정에 실패했습니다');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      queryClient.invalidateQueries({ queryKey: ['field-survey-detail', editId] });
      toast.success('현장 조사가 수정되었습니다');
      navigate('/field-survey/history');
    },
    onError: (error: Error) => { toast.error(error.message); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (deleteId: number) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${deleteId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('삭제에 실패했습니다');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      toast.success('삭제되었습니다');
    },
  });

  const resetForm = () => {
    setStep('address');
    // groupId는 유지 (같은 그룹 연속 조사)
    setAddress(DEFAULT_ADDRESS_PREFIX);
    setDongName('');
    setMemo('');
    setIsAccessRestricted(false);
    setSelectedUnits(new Set());
    setCustomUnits(new Map());
  };

  const toggleUnit = (floor: number, unitNumber: string) => {
    const key = `${floor}_${unitNumber}`;
    setSelectedUnits(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleFloor = (floor: number) => {
    const floorKeys = Array.from({ length: UNITS_PER_FLOOR }, (_, i) => `${floor}_${i + 1}`);
    const allSelected = floorKeys.every(k => selectedUnits.has(k));
    setSelectedUnits(prev => {
      const next = new Set(prev);
      floorKeys.forEach(k => { if (allSelected) next.delete(k); else next.add(k); });
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set<string>();
    FLOORS.forEach(f => {
      for (let u = 1; u <= UNITS_PER_FLOOR; u++) all.add(`${f}_${u}`);
      const customs = customUnits.get(f);
      if (customs) customs.forEach(c => all.add(`${f}_${c}`));
    });
    setSelectedUnits(all);
  };

  const deselectAll = () => setSelectedUnits(new Set());

  const addCustomUnit = (floor: number) => {
    const input = prompt(`${floorLabel(floor)} 커스텀 호수 입력 (예: 1-1, 상가1)`);
    if (!input || !input.trim()) return;
    const unitName = input.trim();
    const existing = customUnits.get(floor) || [];
    if (existing.includes(unitName)) { toast.error('이미 추가된 호수입니다'); return; }
    setCustomUnits(prev => { const next = new Map(prev); next.set(floor, [...(prev.get(floor) || []), unitName]); return next; });
    setSelectedUnits(prev => new Set(prev).add(`${floor}_${unitName}`));
  };

  const removeCustomUnit = (floor: number, unitName: string) => {
    setCustomUnits(prev => { const next = new Map(prev); const list = (prev.get(floor) || []).filter(u => u !== unitName); if (list.length === 0) next.delete(floor); else next.set(floor, list); return next; });
    setSelectedUnits(prev => { const next = new Set(prev); next.delete(`${floor}_${unitName}`); return next; });
  };

  const handleSave = () => {
    const units = Array.from(selectedUnits).map(key => {
      const parsed = parseUnitKey(key);
      return { floor: parsed.floor, unitNumber: String(parsed.unitNumber) };
    });
    const payload = {
      groupId: groupId || null,
      address,
      ...(dongName ? { dongName } : {}),
      ...(memo ? { memo } : {}),
      isAccessRestricted,
      units,
    };
    if (isEditMode) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  };

  const saveMutation = isEditMode ? updateMutation : createMutation;

  const getGroupedUnits = () => {
    const grouped: Record<number, string[]> = {};
    Array.from(selectedUnits).forEach(key => {
      const { floor, unitNumber } = parseUnitKey(key);
      if (!grouped[floor]) grouped[floor] = [];
      grouped[floor].push(unitNumber);
    });
    return FLOORS
      .filter(f => grouped[f] && grouped[f].length > 0)
      .map(f => ({
        floor: f,
        units: grouped[f]!.sort((a, b) => {
          const numA = Number(a), numB = Number(b);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.localeCompare(b);
        }),
      }));
  };

  if (isEditMode && !loaded) {
    return <div className="page-narrow"><p className="text-gray-400 py-12 text-center">로딩 중...</p></div>;
  }

  // Step 1: Address Input
  if (step === 'address') {
    return (
      <div className="page-narrow">
        <h1 className="page-title mb-6">{isEditMode ? '현장 조사 수정' : '현장 조사'}</h1>
        <div className="space-y-3 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">그룹</label>
            <select value={groupId ?? ''} onChange={e => setGroupId(e.target.value ? Number(e.target.value) : null)}
              className="input-field">
              <option value="">미분류</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">도로명 주소</label>
            <input type="text" value={address} onChange={e => setAddress(e.target.value)}
              placeholder="예: 경기도 성남시 분당구 불정로 6" className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">동 이름 (선택)</label>
            <input type="text" value={dongName} onChange={e => setDongName(e.target.value)}
              placeholder="예: A동, 나동, 102동" className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">메모 (선택)</label>
            <input type="text" value={memo} onChange={e => setMemo(e.target.value)}
              placeholder="현장 메모" className="input-field" />
          </div>
          {/* 출입 불가 토글 */}
          <button
            type="button"
            onClick={() => setIsAccessRestricted(!isAccessRestricted)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
              isAccessRestricted
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-white border-gray-200 text-gray-500'
            }`}
          >
            <ShieldOff size={18} />
            <span className="text-sm font-medium">출입 불가</span>
            <div className={`ml-auto w-10 h-6 rounded-full transition-colors flex items-center ${
              isAccessRestricted ? 'bg-red-500 justify-end' : 'bg-gray-300 justify-start'
            }`}>
              <div className="w-5 h-5 bg-white rounded-full shadow-sm mx-0.5" />
            </div>
          </button>

          <button onClick={() => setStep('select')} disabled={!address.trim()}
            className="btn-primary w-full">
            다음 단계 →
          </button>
        </div>

        {!isEditMode && surveys.length > 0 && (
          <div>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">최근 조사</h2>
            <div className="space-y-2">
              {surveys.map((survey) => (
                <div key={survey.id} className="card">
                  <div className="card-body flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{survey.address}</p>
                        {survey.isAccessRestricted && <span className="badge bg-red-100 text-red-600 text-[10px]">출입불가</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {survey.dongName && `${survey.dongName} · `}
                        {survey._count.units}호 · {new Date(survey.createdAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex items-center ml-2">
                      <button onClick={() => navigate(`/field-survey/${survey.id}`)}
                        className="p-2 text-gray-300 active:text-brand-600">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => { if (confirm('삭제하시겠습니까?')) deleteMutation.mutate(survey.id); }}
                        className="p-2 text-gray-300 active:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Step 2: Unit Selection Grid
  if (step === 'select') {
    return (
      <div className="page-narrow pb-36">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => isEditMode ? navigate('/field-survey/history') : setStep('address')}
            className="p-2 -ml-2 text-gray-400 active:text-gray-600 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            {isEditMode && <span className="badge-blue text-[10px] mb-0.5">수정 중</span>}
            <p className="text-sm font-medium text-gray-900 truncate">
              {address}{dongName ? ` (${dongName})` : ''}
            </p>
            {isAccessRestricted && <span className="badge bg-red-100 text-red-600 text-[10px]">출입불가</span>}
          </div>
          <button onClick={() => setStep('address')}
            className="btn-sm btn-secondary text-xs flex-shrink-0">
            <Pencil size={12} className="inline mr-1" />주소 수정
          </button>
        </div>

        <div className="space-y-3">
          {FLOORS.map(floor => {
            const floorKeys = Array.from({ length: UNITS_PER_FLOOR }, (_, i) => `${floor}_${i + 1}`);
            const allSelected = floorKeys.every(k => selectedUnits.has(k));
            const someSelected = floorKeys.some(k => selectedUnits.has(k));
            const floorCustomUnits = customUnits.get(floor) || [];

            return (
              <div key={floor} className="card card-body">
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => toggleFloor(floor)}
                    className={`text-sm font-bold px-2.5 py-1 rounded-lg transition-colors ${
                      allSelected ? 'bg-brand-100 text-brand-700'
                        : someSelected ? 'bg-brand-50 text-brand-600'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}>
                    {floorLabel(floor)}
                  </button>
                  <button onClick={() => addCustomUnit(floor)}
                    className="flex items-center gap-1 text-xs px-2 py-1 text-gray-400 rounded-lg active:bg-gray-100">
                    <Plus size={14} /> 호수
                  </button>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {Array.from({ length: UNITS_PER_FLOOR }, (_, i) => i + 1).map(unitNum => {
                    const key = `${floor}_${unitNum}`;
                    const isSelected = selectedUnits.has(key);
                    return (
                      <button key={key} onClick={() => toggleUnit(floor, String(unitNum))}
                        className={`min-w-[44px] min-h-[44px] rounded-xl text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-brand-500 text-white shadow-sm'
                            : 'bg-gray-50 border border-gray-200 text-gray-600 active:bg-gray-100'
                        }`}>
                        {unitNum}
                      </button>
                    );
                  })}
                </div>
                {floorCustomUnits.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-50">
                    {floorCustomUnits.map(unitName => (
                      <span key={unitName}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-brand-500 text-white text-xs font-medium rounded-lg">
                        {unitName}
                        <button onClick={() => removeCustomUnit(floor, unitName)}
                          className="ml-0.5 rounded-full w-4 h-4 flex items-center justify-center hover:bg-brand-600">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px))] sm:bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-100 p-4 z-30">
          <div className="max-w-page mx-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">
                선택 <span className="text-brand-600 font-bold text-base">{selectedUnits.size}</span>호
              </span>
              <div className="flex gap-2">
                <button onClick={selectAll} className="btn-sm btn-secondary">전체선택</button>
                <button onClick={deselectAll} className="btn-sm btn-secondary">전체해제</button>
              </div>
            </div>
            <button onClick={() => setStep('confirm')} disabled={selectedUnits.size === 0}
              className="btn-primary w-full">
              {isEditMode ? '수정 확인' : '저장하기'} → ({selectedUnits.size}호)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Confirmation
  if (step === 'confirm') {
    const grouped = getGroupedUnits();
    return (
      <div className="page-narrow">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setStep('select')}
            className="p-2 -ml-2 text-gray-400 active:text-gray-600 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <h1 className="page-title">최종 확인</h1>
        </div>

        <div className="card card-body mb-4">
          {groupId && groups.find(g => g.id === groupId) && (
            <span className="badge-green mb-2">{groups.find(g => g.id === groupId)!.name}</span>
          )}
          <p className="text-xs font-medium text-brand-600 mb-1">도로명 주소</p>
          <p className="text-base font-bold text-gray-900">{address}</p>
          {dongName && <p className="text-sm text-gray-500 mt-0.5">동: {dongName}</p>}
          {memo && <p className="text-sm text-gray-500 mt-0.5">메모: {memo}</p>}
          {isAccessRestricted && (
            <span className="inline-flex items-center gap-1 mt-2 badge bg-red-100 text-red-600">
              <ShieldOff size={12} /> 출입 불가
            </span>
          )}
        </div>

        <div className="card card-body mb-6">
          <p className="text-sm text-gray-500 mb-3">
            총 <span className="text-brand-600 font-bold text-lg">{selectedUnits.size}</span>호 선택
          </p>
          <div className="space-y-2">
            {grouped.map(({ floor, units }) => (
              <div key={floor} className="flex items-start">
                <span className="text-xs font-bold text-gray-400 w-7 flex-shrink-0 pt-0.5">{floorLabel(floor)}</span>
                <div className="flex flex-wrap gap-1 ml-2">
                  {units.map(u => (
                    <span key={u} className="badge-blue">{unitDisplayCode(floor, u)}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <button onClick={handleSave} disabled={saveMutation.isPending}
            className="btn-success w-full">
            {saveMutation.isPending ? '저장 중...' : isEditMode ? '수정 저장' : '최종 저장'}
          </button>
          <button onClick={() => setStep('select')} className="btn-secondary w-full">
            수정하기 ←
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default FieldSurveyPage;
